// menu.service.ts

import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { DateTime } from 'luxon';
import { EMPTY, Subject } from 'rxjs';
import { catchError, retry, switchMap } from 'rxjs/operators';
import { Menu, Recommendations, Section, SuperSectionVM } from '../models/menu.model';
import { ApiService } from '../services/api.service';
import { DataService } from '../services/data.service';
import { hasEnabledItems, nextScheduleBoundary, resolveMenu, toSuperSectionVM } from '../menu/menu.utils';

@Injectable({
  providedIn: 'root',
})
export class MenuService {
  private readonly apiService = inject(ApiService);
  private readonly dataService = inject(DataService);

  private readonly rawMenu = signal<Menu | null>(null);
  private readonly refreshRequests = new Subject<string>();
  // Ticks forward exactly when a schedule crosses a boundary (see reschedule() below),
  // so `menu` below re-resolves without needing to poll on a timer.
  private readonly now = signal(DateTime.now());
  private wakeupTimeoutId: ReturnType<typeof setTimeout> | null = null;

  isGridView = signal(false);

  // The menu as it should currently be shown/priced: schedule-inactive sections dropped,
  // active priced schedules (e.g. happy hour) applied to item prices. Everything else in the
  // app reads this, never rawMenu, so schedule handling lives in exactly one place.
  readonly menu = computed(() => {
    const menu = this.rawMenu();
    return menu ? resolveMenu(menu, this.now()) : null;
  });

  readonly superSections = computed<SuperSectionVM[]>(() =>
    (this.menu()?.sections ?? []).filter(hasEnabledItems).map(toSuperSectionVM));

  readonly recommendations = computed<Recommendations | null>(() => this.menu()?.recommendations ?? null);

  constructor() {
    effect(() => {
      this.now(); // re-read once our own wakeup fires below, to arm the *next* boundary
      this.reschedule(this.rawMenu());
    });

    // Every "the menu may be stale" trigger - a menu.updated socket event, a socket
    // reconnect, the server rejecting items at checkout - funnels through one pipeline, so
    // a newer refresh always cancels an older one still in flight, whatever triggered it.
    this.refreshRequests.pipe(
      switchMap(zoneId => this.apiService.getMenuCached(zoneId).pipe(
        retry(1),
        catchError(error => {
          console.error('Failed to refresh menu', error);
          return EMPTY;
        }),
      )),
    ).subscribe(menu => this.setMenu(menu));
  }

  setMenu(menu: Menu): void {
    this.rawMenu.set(menu);
  }

  // Refetches the menu (for the current zone unless one is given). Cart lines follow on their
  // own - TableSocketService syncs them from the `menu` signal whenever it changes.
  refresh(zoneId: string | undefined = this.dataService.zone()?.id): void {
    if (zoneId) this.refreshRequests.next(zoneId);
  }

  getSuperSection(id: string): Section | null {
    return this.menu()?.sections.find(section => section.id === id) ?? null;
  }

  private reschedule(menu: Menu | null): void {
    if (this.wakeupTimeoutId !== null) {
      clearTimeout(this.wakeupTimeoutId);
      this.wakeupTimeoutId = null;
    }
    if (!menu) return;

    const boundary = nextScheduleBoundary(menu, DateTime.now());
    if (!boundary) return;

    const delayMs = Math.max(boundary.diff(DateTime.now()).as('milliseconds'), 0);
    this.wakeupTimeoutId = setTimeout(() => this.now.set(DateTime.now()), delayMs);
  }
}
